import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { CART, CHANGE_CURRENCY, CURRENCY_ID } from "../../../../../constants/common/common";
import { mobileIcons, mobileIconsHome } from "../../../../../constants/common/headerToolbarConstants";
import IconButtonComponent from "../../../../atoms/iconButton";
import CurrencyList from "./currencyList";
import Drawer from "../../../../molecules/drawer";
import Typography from "../../../../atoms/typography";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { unlockBody } from "../../../../../utils/document";
import useAnalytics from "../../../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../../../analytics/clevertapConstants";
import { usePathname } from "next/navigation";
import { getIconUrl, pageName, buildIconTintFilter } from "../../../../../utils/common";
import { setIsMobileSearchVisibleAction } from "../../../../../store/slices/commonSlice";
import { ROUTE_CONSTANTS } from "../../../../../constants/route";
import Portal from "../../../../molecules/Portal";

/**
 * Activates a focusable toolbar icon on Enter or Space (div-as-button keyboard support).
 * @param {KeyboardEvent} event - Keyboard event from the icon container
 * @param {() => void} onActivate - Callback that runs the icon's click action
 * @returns {void}
 */
function onIconKeyDown(event, onActivate) {
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }
  event.preventDefault();
  onActivate();
}

/**
 * MobileToolBar component renders a toolbar specifically designed for mobile devices.
 * It provides options for accessing cart and currency settings. The component manages
 * the display of the cart count and allows users to select their preferred currency.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {number} props.cartCount - The current count of items in the cart.
 * @param {boolean} [props.isSearchIconVisible=false] - Whether to show the search icon.
 * @param {string} [props.headerTextIconColor] - Optional tint color for toolbar icons (masthead).
 * @returns {JSX.Element} The MobileToolBar component.
 */
const MobileToolBar = ({ cartCount, isSearchIconVisible = false, headerTextIconColor }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const pathname = usePathname();
  const [selectedCurrency, setSelectedCurrency] = useState(CURRENCY_ID);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const userSelectedCurrency = useSelector((state) => state?.currencyList?.userSelectedCurrency);
  const { product, currencyFlagIcon } = useSelector(
    (state) => ({
      product: state?.pdp?.clientCurrentClickedItem,
      currencyFlagIcon: state?.currencyList?.currencyFlagIcon || [],
    }),
    shallowEqual,
  );
  const urlIdentifier = product?.urlIdentifier;
  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");

  const commonData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    menu_name: null,
  };

  useEffect(() => {
    if (userSelectedCurrency?.currency?.id) {
      setSelectedCurrency(userSelectedCurrency.currency.id);
    }
  }, [userSelectedCurrency]);

  /**
   * This function handles the tab click based on the provided tab name.
   *
   * @function
   * @param {string} tabName - The name of the tab to handle click events for.
   * @returns {void}
   */
  const onTabClick = useCallback(
    (tabName) => {
      if (tabName) {
        trackEvent(cleverTapCommonEvents.headerMenuClicked, { ...commonData, menu_name: tabName }, ["clevertap"]);
      }
      switch (tabName) {
        case "search":
          dispatch(setIsMobileSearchVisibleAction(true));
          break;
        case "cart":
          window.location.href = `/${ROUTE_CONSTANTS.CHECKOUT}/${ROUTE_CONSTANTS.DELIVERY_ADDRESS}`;
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartCount],
  );

  /**
   * Returns currency icon if id is "currency", otherwise the fallback URL.
   */
  const setIconSrc = (url, id) => {
    if (currencyFlagIcon.length && selectedCurrency && id === "currency") {
      return currencyFlagIcon?.find((item) => item?.countryCurrency === selectedCurrency)?.currencyIcon;
    }
    return url;
  };

  /**
   * Handles hiding the currency dropdown when a currency is selected,
   * and updates the selected currency.
   *
   * @param {string} currency - The selected currency ID.
   * @returns {void}
   */
  const handleCurrencySelect = (currency) => {
    setSelectedCurrency(currency);
    setIsDropdownVisible(false);
    unlockBody();
    onTabClick(null);
  };

  /**
   * Handles click events on different tabs.
   * Opens the currency dropdown when the "currency" tab is clicked.
   *
   * @param {string} id - The ID of the clicked tab.
   * @returns {void}
   */
  const onHandleClick = (id) => {
    onTabClick(id);
    if (id === "currency") {
      setIsDropdownVisible(true);
    }
    trackEvent(cleverTapCommonEvents.headerMenuClicked, { ...commonData, menu_name: id }, ["clevertap"]);
  };

  /**
   * Handles closing of the currency dropdown drawer.
   *
   * @returns {void}
   */
  const onDrawerClose = () => {
    setIsDropdownVisible(false);
  };

  /**
   * Renders a mobile toolbar icon. Uses `<img src>` and optionally tints via `headerTextIconColor`.
   * @param {object} item - Toolbar icon config
   * @param {number} index - List index
   * @returns {JSX.Element}
   */
  const renderMobileIcons = (item, index) => {
    const iconSrc = setIconSrc(item?.url, item?.id);
    const iconSize = item?.width || 24;
    const resolvedSrc = getIconUrl(iconSrc);
    const altText = item?.label || item?.id || "icon";
    const shouldTintIcon = Boolean(headerTextIconColor);

    /**
     * Runs the toolbar action for this icon (cart / currency / search).
     * @returns {void}
     */
    const onIconClick = () => onHandleClick(item?.id);

    /**
     * Keyboard activation handler for this icon wrapper.
     * @param {KeyboardEvent} event - Keydown event
     * @returns {void}
     */
    const onKeyDown = (event) => onIconKeyDown(event, onIconClick);

    return (
      <div
        key={index}
        className="relative flex cursor-pointer items-center justify-center"
        tabIndex="0"
        id={item?.id}
        onClick={onIconClick}
        onKeyDown={onKeyDown}
        role="button"
        aria-label={altText}
      >
        {item?.label === CART && cartCount > 0 && (
          <span className="absolute bottom-13 left-20 z-[999] flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-notification-secondary to-notification-primary p-4 text-10 font-500 text-white-900">
            {cartCount}
          </span>
        )}
        {shouldTintIcon ? (
          <img
            src={resolvedSrc}
            alt={item?.label || "Menu icon"}
            width={iconSize}
            height={iconSize}
            className="mx-2 object-contain xxs:mx-6"
            style={{
              width: iconSize,
              height: iconSize,
              filter: buildIconTintFilter(headerTextIconColor),
            }}
          />
        ) : (
          <IconButtonComponent
            labelColor="w-full font-normal"
            classes="py-2 flex flex-col justify-center items-center gap-2"
            imgWidth={item?.width}
            iconSrc={iconSrc}
            isPriority={false}
            imageContainerMargin={"mx-2 xxs:mx-6 flex items-center"}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {isSearchIconVisible && renderMobileIcons(mobileIcons?.[0], 0)}
      {mobileIconsHome?.map((item, index) => renderMobileIcons(item, index))}
      <Portal rootId="fnpRootId" renderOnDidMount={true}>
        <Drawer
          position="bottom"
          isOpen={isDropdownVisible}
          onDrawerClose={onDrawerClose}
          styles="px-16 max-h-[90vh] pb-0"
        >
          <div className="flex flex-col gap-8">
            <Typography Tag={"h4"} variant="h6" classes="text-16 font-600 mb-8">
              {CHANGE_CURRENCY}
            </Typography>
            <div className="mb-16 max-h-[calc(70vh-34px)] overflow-auto rounded-8 border-1 border-thinLightGrey">
              <CurrencyList setSelectedCurrency={handleCurrencySelect} selectedCurrency={selectedCurrency} />
            </div>
          </div>
        </Drawer>
      </Portal>
    </>
  );
};

MobileToolBar.propTypes = {
  cartCount: PropTypes.number,
  isSearchIconVisible: PropTypes.bool,
  headerTextIconColor: PropTypes.string,
};

export default MobileToolBar;
